import { useEffect, useRef, useState } from 'react';
import { api, wertDeutsch } from '../api.ts';
import { Auswahl, Fehlerkasten, ausFehler, type Fehlerlage } from './Formular.tsx';
import { useFrisch } from './Frisch.tsx';
import { fragen } from './rueckfrage.tsx';

/**
 * BEREICHE AUF DEM SCAN ANLERNEN (.271).
 *
 * ORIGINALTEXT MARVIN (01.10.2026)
 *   „dann stelle mir das vor, dass du das dokument ocr einliest, anzeigt und
 *    dann kann man die wichtigen bereiche definieren, damit er weiß wo er beim
 *    scan drauf achten soll."
 *
 * Die Seite steht als Bild da. Mit der Maus wird ein Rahmen um die Stelle
 * gezogen, rechts das Feld gewählt; der Server liest NUR diesen Ausschnitt
 * (Texterkennung) und zeigt, was er daraus macht. Gespeichert wird die STELLE
 * in Anteilen der Seite, nie der Wert. Danach liest er die offenen Papiere
 * desselben Ausstellers neu.
 */
interface Rahmen { x: number; y: number; b: number; h: number }

const FARBEN: Record<string, string> = {
  anschrift: '#1f6feb', kls_id: '#2da44e', sm_nummer: '#8250df', aktenzeichen: '#bf8700',
  gebuehr: '#cf222e', zahlungsziel: '#d1242f', gueltig_von: '#0969da', gueltig_bis: '#0969da',
  zeitraum: '#0969da', kassenzeichen: '#9a6700',
};

export function Bereichsleser({ eingangId, art, vordruck }: { eingangId: string; art: string; vordruck: string }) {
  const [d, setD] = useState<any>(null);
  const [seite, setSeite] = useState(1);
  const [rahmen, setRahmen] = useState<Rahmen | null>(null);
  const [zieht, setZieht] = useState<{ x: number; y: number } | null>(null);
  const [feld, setFeld] = useState('');
  const [probe, setProbe] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [stempel, setStempel] = useState(0);
  const flaeche = useRef<HTMLDivElement>(null);

  async function laden() {
    try { setD(await api.lesebereiche(eingangId, art, vordruck)); setFehler(null); }
    catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, [eingangId, art, vordruck]);
  /* WER ANDERSWO EINEN BEREICH SPEICHERT, SIEHT IHN HIER AUCH. */
  useFrisch(laden);

  /* DER PROBELAUF LÄUFT VON SELBST, sobald Rahmen und Feld stehen. */
  useEffect(() => {
    if (!rahmen || !feld) { setProbe(null); return; }
    let weg = false;
    setLaeuft(true);
    api.lesebereichProbe({ eingang_id: eingangId, feld, seite, ...rahmen })
      .then((p) => { if (!weg) setProbe(p); })
      .catch((f) => { if (!weg) setFehler(ausFehler(f)); })
      .finally(() => { if (!weg) setLaeuft(false); });
    return () => { weg = true; };
  }, [rahmen, feld, seite]);

  const anteil = (e: React.MouseEvent) => {
    const r = flaeche.current!.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
             y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
  };
  const zonenDerSeite = (d?.zonen ?? []).filter((z: any) => Number(z.seite) === seite);
  const feldtitel = (k: string) => (d?.felder ?? []).find((f: any) => f.schluessel === k)?.titel ?? k;

  return (
    <div>
      <Fehlerkasten lage={fehler} />
      {hinweis && <div className="hinweis">✅ {hinweis}</div>}
      {d?.ocr && !d.ocr.gelesen && (
        <div className="hinweis warnung">
          <b>Die Texterkennung hat aus diesem Scan noch nichts gelesen.</b>{' '}
          {d.ocr.fehler ?? ''} ({d.ocr.versuche} Versuch{d.ocr.versuche === 1 ? '' : 'e'})
        </div>
      )}
      <div className="hinweis">
        <b>So geht es</b>{' '}Mit der Maus einen Rahmen um die Stelle ziehen (zum Beispiel die Baustellenanschrift
        oder das Aktenzeichen), rechts das Feld wählen, prüfen was gelesen wird, speichern. Gilt für jeden Scan von
        {' '}<b>{d?.vordruck ?? vordruck}</b>.
      </div>

      <div className="nebeneinander breitschmal">
        <div>
          <div className="reihe" style={{ gap: 6, marginBottom: 6 }}>
            {Array.from({ length: Math.min(d?.seiten ?? 1, 12) }, (_, i) => i + 1).map((n) => (
              <button key={n} className={'knopf klein' + (n === seite ? ' haupt' : '')}
                      onClick={() => { setSeite(n); setRahmen(null); }}>Seite {n}</button>
            ))}
            <button className="knopf klein" disabled={laeuft} onClick={async () => {
              setLaeuft(true); setFehler(null);
              try {
                const r = await api.leseregelNeuLesen(eingangId);
                setHinweis(`Neu gelesen: ${r.zeichen} Zeichen erkannt${r.auftrag_id ? ', jetzt zugeordnet' : ''}.`);
                setStempel(Date.now()); await laden();
              } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
            }}>Texterkennung neu</button>
          </div>
          <div ref={flaeche} className="bereichsflaeche"
               style={{ position: 'relative', userSelect: 'none', cursor: 'crosshair', border: '1px solid var(--rand, #ccc)' }}
               onMouseDown={(e) => { e.preventDefault(); const p = anteil(e); setZieht(p); setRahmen({ x: p.x, y: p.y, b: 0, h: 0 }); }}
               onMouseMove={(e) => {
                 if (!zieht) return;
                 const p = anteil(e);
                 setRahmen({ x: Math.min(zieht.x, p.x), y: Math.min(zieht.y, p.y), b: Math.abs(p.x - zieht.x), h: Math.abs(p.y - zieht.y) });
               }}
               onMouseUp={() => { setZieht(null); setRahmen((r) => r && r.b > 0.01 && r.h > 0.005 ? { ...r } : null); }}
               onMouseLeave={() => setZieht(null)}>
            <img src={`/api/leseregel/eingang/${eingangId}/seite/${seite}?t=${stempel}`} alt={`Seite ${seite}`}
                 style={{ width: '100%', display: 'block' }} draggable={false} />
            {zonenDerSeite.map((z: any) => (
              <div key={z.id} title={feldtitel(z.feld)} style={{
                position: 'absolute', left: `${z.x * 100}%`, top: `${z.y * 100}%`, width: `${z.b * 100}%`, height: `${z.h * 100}%`,
                border: `2px solid ${FARBEN[z.feld] ?? '#555'}`, background: 'rgba(255,255,255,0.08)', pointerEvents: 'none' }}>
                <span className="klein" style={{ position: 'absolute', top: -18, left: 0, background: FARBEN[z.feld] ?? '#555',
                  color: '#fff', padding: '0 4px', borderRadius: 3, whiteSpace: 'nowrap' }}>{feldtitel(z.feld)}</span>
              </div>
            ))}
            {rahmen && rahmen.b > 0 && (
              <div style={{ position: 'absolute', left: `${rahmen.x * 100}%`, top: `${rahmen.y * 100}%`,
                width: `${rahmen.b * 100}%`, height: `${rahmen.h * 100}%`, border: '2px dashed #e36209',
                background: 'rgba(227,98,9,0.12)', pointerEvents: 'none' }} />
            )}
          </div>
        </div>

        <div>
          <h3>Das Feld</h3>
          <Auswahl name="feld" titel="Was steht in dem Rahmen?" wert={feld} setze={setFeld} breite={300}
                   leerText="bitte wählen"
                   optionen={(d?.felder ?? []).map((f: any) => ({ wert: f.schluessel, text: f.titel }))} />
          <h3>Was gelesen wird</h3>
          {!rahmen && <div className="leise">Ziehen Sie links einen Rahmen.</div>}
          {rahmen && !feld && <div className="leise">Wählen Sie oben das Feld.</div>}
          {rahmen && feld && laeuft && <div className="leise">Wird gelesen …</div>}
          {rahmen && feld && !laeuft && probe && (
            <>
              <pre className="lesetext" style={{ whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto' }}>{probe.text || '(nichts)'}</pre>
              <div className={'hinweis' + (probe.wert === null ? ' fehler' : '')}>
                {probe.wert === null ? probe.grund : <>Wert: <b>{wertDeutsch(probe.wert)}</b></>}
              </div>
            </>
          )}
          <div className="reihe" style={{ marginTop: 10 }}>
            <button className="knopf haupt" disabled={laeuft || !rahmen || !feld || !probe || probe.wert === null}
                    onClick={async () => {
                      setLaeuft(true); setFehler(null);
                      try {
                        const r = await api.lesebereichSpeichern({ eingang_id: eingangId, feld, seite, art, vordruck: d?.vordruck ?? vordruck, ...rahmen! });
                        setHinweis(r.klartext); setRahmen(null); setFeld(''); setProbe(null); await laden();
                      } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
                    }}>Bereich speichern</button>
            {rahmen && <button className="knopf" onClick={() => { setRahmen(null); setProbe(null); }}>Rahmen weg</button>}
          </div>

          <h3>Schon angelernt für {d?.vordruck ?? vordruck}</h3>
          {!(d?.zonen ?? []).length && <div className="leise">Noch kein Bereich.</div>}
          {(d?.zonen ?? []).map((z: any) => (
            <div key={z.id} className="reihe" style={{ gap: 8, alignItems: 'center' }}>
              <span style={{ width: 10, height: 10, background: FARBEN[z.feld] ?? '#555', display: 'inline-block', borderRadius: 2 }} />
              <span>{feldtitel(z.feld)}, Seite {z.seite}</span>
              <button className="knopf klein" disabled={laeuft} onClick={async () => {
                if (!await fragen(`Den Bereich „${feldtitel(z.feld)}" wegnehmen?`)) return;
                try { await api.lesebereichWeg(z.id); await laden(); } catch (f) { setFehler(ausFehler(f)); }
              }}>weg</button>
            </div>
          ))}

          <details style={{ marginTop: 12 }}>
            <summary className="klein">Der ganze erkannte Text ({String(d?.text ?? '').replace(/\s+/g, '').length} Zeichen)</summary>
            <pre className="lesetext klein" style={{ whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>{d?.text || '(nichts erkannt)'}</pre>
          </details>
        </div>
      </div>
    </div>
  );
}
